/**
 * frontend/src/components/Layout/Header.js
 * 
 * Horizontal application header component.
 * Displays the application logo, primary navigation links, and a sign-out button.
 * 
 * Common functions:
 * - handleLogout: Triggers the logout process and redirects to the login page.
 */
import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { FiLogOut } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import icon from '../../assets/icon.png';

const headerStyles = {
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 32px',
    background: 'white',
    borderBottom: '1px solid #e2e8f0',
    position: 'fixed',
    top: 0,
    left: '72px',
    right: 0,
    zIndex: 100,
    height: '64px',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    textDecoration: 'none',
    color: '#1e293b',
    fontWeight: '700',
    fontSize: '1.25rem',
  },
  logoIcon: {
    width: '32px',
    height: '32px',
  },
  nav: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  navLink: {
    padding: '8px 16px',
    borderRadius: '8px',
    textDecoration: 'none',
    color: '#64748b',
    fontSize: '0.9rem',
    fontWeight: '500',
    transition: 'all 0.2s ease',
  },
  navLinkActive: {
    color: '#1e293b',
    background: '#f1f5f9',
  },
  signOutBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    background: 'white',
    color: '#64748b',
    fontSize: '0.9rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
};

const Header = ({ workspace }) => {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isAdmin = workspace?.user_role === 'admin';

  return (
    <header style={headerStyles.header}>
      <a href="/" style={headerStyles.logo}>
        <img src={icon} alt="ChangeFox" style={headerStyles.logoIcon} />
        <span>ChangeFox</span>
      </a>

      <nav style={headerStyles.nav}>
        <NavLink
          to="/dashboard"
          end
          style={({ isActive }) => ({
            ...headerStyles.navLink,
            ...(isActive ? headerStyles.navLinkActive : {}),
          })}
        >
          Dashboard
        </NavLink>

        {isAdmin && (
          <NavLink
            to="/dashboard/settings"
            style={({ isActive }) => ({
              ...headerStyles.navLink,
              ...(isActive ? headerStyles.navLinkActive : {}),
            })}
          >
            Settings
          </NavLink>
        )}

        <button
          style={headerStyles.signOutBtn}
          onClick={handleLogout}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#f8fafc';
            e.currentTarget.style.borderColor = '#cbd5e1';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'white';
            e.currentTarget.style.borderColor = '#e2e8f0';
          }}
        >
          <FiLogOut size={16} />
          Sign Out
        </button>
      </nav>
    </header>
  );
};

export default Header;
